import { useChartTitle, useReportId } from 'hooks/helperHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ChartSidebarSummary from 'components/ReportShelf/ChartSidebarSummary';
import ReportBar from 'components/ReportBar/ReportBar';
import ReportPage from 'components/ReportPage/ReportPage';
import ReportShelf from 'components/ReportShelf/ReportShelf';
import SingleSessionConfig from './SingleSessionConfig';
import SingleSessionSearchCriteria from './SingleSessionSearchCriteria';
import { Typography } from '@trackman/web-shared-components';
import UmpireSingleSessionHeader from 'components/ReportPageHeader/UmpireSingleSessionHeader';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useTranslation } from 'react-i18next';
import useUmpireSingleSessionReport from 'hooks/UmpireReport/SingleSession/useUmpireSingleSessionReport';
import LoadingModal from 'components/LoadingModal/LoadingModal';
import { useChartVisibility } from 'hooks/useChartVisibility';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';

const UmpireReportSingleSessionPage = () => {
  const { isCreate } = useReportId();

  const { t } = useTranslation();

  const {
    isEditMode,
    setIsEditMode,

    searchCriteria,
    searchChips,

    tempLayout,
    chartData,

    isLoadingReport,
    generateReport,
    getNewData,
    setTempLayout,

    selectedConfgValues,
    specialConfigOptions,

    saveLayoutForSession,
    saveLayoutForOrg,

    resetStore,
    editingChart,
    setEditingChart,

    hasUnsavedEdits,
    discardLayoutChanges,
  } = useUmpireSingleSessionReport();

  const [openSection, setOpenSection] = useState<'searchCriteria' | 'config'>(isCreate ? 'searchCriteria' : 'config');
  const [parent] = useAutoAnimate();

  const downloadName = useMemo(() => {
    const { leagueNames = [], seasonNames = [], umpireNames = [], sessionName = '' } = searchChips;

    const leagueString = leagueNames ? ' - ' + leagueNames.slice(0, 1).join('; ') + (leagueNames.length > 1 ? '...' : '') : '';
    const umpireString = umpireNames ? ' - ' + umpireNames.slice(0, 1).join('; ') + (umpireNames.length > 1 ? '...' : '') : '';
    const timeString = seasonNames ? seasonNames.slice(0, 3).join(', ') + ' ' : '';
    const sessionString = sessionName ? ' - ' + sessionName : '';

    return `${timeString}Umpire Single Session Report${leagueString}${umpireString}${sessionString}`;
  }, [searchChips]);

  const { reset: resetChartBrokerStore } = useChartBrokerStore();

  useEffect(() => {
    return () => {
      resetStore();
      resetChartBrokerStore();
    };
  }, [resetStore, resetChartBrokerStore]);

  const { getChartTitle, getChartStats } = useChartTitle();

  const { visibleCharts, hiddenCharts, toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);

  const handleGenerate = useCallback(() => {
    setOpenSection('config');
    generateReport();
  }, [generateReport]);

  const handleToggleVisibility = useCallback(
    (chartId: string) => {
      toggleChartVisibility(chartId);
    },
    [toggleChartVisibility]
  );

  return (
    <>
      <ReportBar
        reportName={t('UmpireReport.SingleSession')}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        downloadName={downloadName}
        saveLayoutForSession={saveLayoutForSession}
        saveLayoutForOrg={saveLayoutForOrg}
        hasUnsavedEdits={hasUnsavedEdits}
        discardLayoutChanges={discardLayoutChanges}
      />

      <div className='report-layout-content'>
        <ReportShelf
          onGenerate={handleGenerate}
          onModify={getNewData}
          isLoadingReport={isLoadingReport}
          disableGenerate={isLoadingReport || !searchCriteria.session}
          editingChart={editingChart}
          setEditingChart={setEditingChart}
          isEditMode={isEditMode}
        >
          <div className='report-layout-shelf' ref={parent}>
            {tempLayout && isEditMode ? (
              <>
                <div className='report-layout-shelf-section'>
                  {!editingChart && (
                    <Typography component='div' variant='body bold'>
                      {t('Report elements')}
                    </Typography>
                  )}
                  {visibleCharts.map((config) => {
                    const { chartId, chartDrawingInfo, ...extraChartData } = config;
                    const { Sidebar } = chartIdTochartSetup[chartId];
                    if (!editingChart) {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={Sidebar ? setEditingChart : undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={config.isHidden ?? false}
                        />
                      );
                    }
                    return Sidebar && editingChart === chartId ? (
                      <Sidebar
                        extraChartData={extraChartData as never}
                        setTempLayout={setTempLayout as any}
                        tempLayout={tempLayout as any}
                      />
                    ) : null;
                  })}
                </div>
                {!editingChart && hiddenCharts.length > 0 && (
                  <div className='report-layout-shelf-section'>
                    <Typography component='div' variant='body bold'>
                      {t('Extra elements')}
                    </Typography>
                    {hiddenCharts.map((config) => {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={true}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <SingleSessionSearchCriteria
                  isSearchCriteriaOpen={openSection === 'searchCriteria'}
                  setSearchCriteriaOpen={() => setOpenSection('searchCriteria')}
                />
                <SingleSessionConfig isConfigOpen={openSection === 'config'} setConfigOpen={() => setOpenSection('config')} />
              </>
            )}
          </div>
        </ReportShelf>
        <div className='report-layout-grid-container'>
          {tempLayout &&
            (chartData ?? []).map((chart, i) => (
              <ReportPage
                key={i}
                layout={tempLayout}
                page={i + 1}
                header={
                  <UmpireSingleSessionHeader
                    umpireName={chart.umpireName}
                    sessionName={searchChips.sessionName}
                    pitchTypes={
                      selectedConfgValues.pitchGrouping === 'Types'
                        ? selectedConfgValues.pitchTagging === 'Auto'
                          ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypes.filter((x) =>
                              selectedConfgValues.pitchTypes.includes(x)
                            ) ?? [])
                          : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypes.filter((x) =>
                              selectedConfgValues.pitchTypes.includes(x)
                            ) ?? [])
                        : undefined
                    }
                    pitchTypeGroups={
                      selectedConfgValues.pitchGrouping === 'Groups'
                        ? selectedConfgValues.pitchTagging === 'Auto'
                          ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypeGroups.filter((x) =>
                              selectedConfgValues.pitchTypeGroups.includes(x)
                            ) ?? [])
                          : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypeGroups.filter((x) =>
                              selectedConfgValues.pitchTypeGroups.includes(x)
                            ) ?? [])
                        : undefined
                    }
                    pitcherHandedness={selectedConfgValues.pitcherHandedness}
                    batterHandedness={selectedConfgValues.batterHandedness}
                    zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                    callCorrectness={selectedConfgValues.includedCallCorrectness}
                    umpireCall={selectedConfgValues.includedCalls}
                  />
                }
                pageChartData={chart}
                isEditMode={isEditMode}
                isLoadingData={isLoadingReport}
                setTempLayout={setTempLayout}
                setEditingChart={setEditingChart}
                editingChart={editingChart}
              />
            ))}
          {tempLayout && (chartData == null || chartData.length === 0) && (
            <ReportPage
              layout={tempLayout}
              page={1}
              header={
                <UmpireSingleSessionHeader
                  pitchTypes={selectedConfgValues.pitchGrouping === 'Types' ? [] : undefined}
                  pitchTypeGroups={selectedConfgValues.pitchGrouping === 'Groups' ? [] : undefined}
                  pitcherHandedness={selectedConfgValues.pitcherHandedness}
                  batterHandedness={selectedConfgValues.batterHandedness}
                  zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                  callCorrectness={selectedConfgValues.includedCallCorrectness}
                  umpireCall={selectedConfgValues.includedCalls}
                />
              }
              pageChartData={undefined}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </div>

      <LoadingModal isLoadingReport={isLoadingReport} />
    </>
  );
};

export default UmpireReportSingleSessionPage;
